import { loadAuthenticatedMedia, MEDIA_ERROR, MEDIA_FORBIDDEN, MEDIA_UNAVAILABLE } from '@/api/authenticatedMedia';

export const DOCUMENT_LOAD_TIMEOUT_MS = 30000;
const aborted = () => new DOMException('Document request cancelled', 'AbortError');

// The deadline includes response-body consumption. Cancellation is an ownership
// fence even when transport or a substituted body reader ignores AbortSignal.
export function loadDocumentResource(url, { signal } = {}) {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    let settled = false;
    let timer;
    const finish = (result, error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      controller.abort();
      if (error) reject(error); else resolve(result);
    };
    const cancel = () => finish(null, aborted());
    if (signal?.aborted) { cancel(); return; }
    signal?.addEventListener('abort', cancel, { once: true });
    timer = setTimeout(() => finish({ state: MEDIA_ERROR, reason: 'timeout' }), DOCUMENT_LOAD_TIMEOUT_MS);
    Promise.resolve().then(() => {
      if (settled) return null;
      return loadAuthenticatedMedia(url, { signal: controller.signal });
    }).then(result => finish(result), error => {
      if (signal?.aborted || error?.name === 'AbortError') finish(null, aborted());
      else finish({ state: MEDIA_ERROR });
    });
  });
}

export function documentFailureMessage(result) {
  if (result?.reason === 'timeout') return 'Document loading timed out. Please try again.';
  if (result?.reason === 'unverified') return 'This offline document could not be verified. Please reconnect and try again.';
  if (result?.state === MEDIA_FORBIDDEN) return 'You do not have permission to view this document.';
  if (result?.reason === 'not_kept') return 'This document was not kept for offline use.';
  if (result?.reason === 'downloading') return 'This document is still downloading for offline use. Please try again.';
  if (result?.state === MEDIA_UNAVAILABLE) return 'This document is unavailable.';
  return 'Unable to open document. Please try again.';
}
